import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { localeLabels, type Locale } from '@sonasi/shared-types';
import { moment } from '@/lib/dates';
import {
  TICKET_KINDS,
  TICKET_STATUSES,
  statusLabel,
  useAddTicketNote,
  useReplyToTicket,
  useSetTicketStatus,
  useTicket,
  type TicketDetail,
  type TicketMessage,
  type TicketStatus,
} from '@/lib/tickets';

/**
 * One report, everything written on it, and the two things the studio can
 * add: an answer, which is emailed, and a note, which is not.
 *
 * ⚠️ **A reply is written down before it is sent**, and the screen says
 * which of the two happened. The mail server belongs to somebody else; when
 * it is down the answer still exists and can be sent again, and a screen
 * that implied otherwise would have somebody retyping what they already
 * wrote.
 */

const panel = 'flex flex-col gap-3 rounded-xl border p-4';
const panelStyle = { borderColor: 'var(--line)', background: 'var(--panel)' };

/** A short, plain label above a value. */
const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <span className="flex flex-col gap-1">
    {/* `field-label`, not `eyebrow`: one names a value, the other names a card. */}
    <span className="field-label">{label}</span>
    <span className="text-[13px] break-words">{children}</span>
  </span>
);

const WHO: Record<TicketMessage['kind'], string> = {
  VISITOR: 'They wrote',
  STUDIO: 'We answered',
  NOTE: 'Note to ourselves',
};

function Message({ message }: { message: TicketMessage }) {
  const note = message.kind === 'NOTE';
  return (
    <article
      className="flex flex-col gap-1.5 rounded-[10px] border p-3.5"
      style={{
        borderColor: note ? 'var(--line)' : 'var(--line2)',
        /* A note is the studio talking to itself, and looks it. */
        background: note ? 'transparent' : 'var(--panel2)',
        borderStyle: note ? 'dashed' : 'solid',
      }}
    >
      <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-[12px] font-medium">{WHO[message.kind]}</span>
        {message.authorName ? (
          <span className="text-[11.5px]" style={{ color: 'var(--muted)' }}>
            {message.authorName}
          </span>
        ) : null}
        <span className="text-[11px]" style={{ color: 'var(--dim)' }}>
          {moment(message.createdAt)}
        </span>
        {/*
          Only ever said about a reply, and only when it failed: "sent" on
          every answer is noise, but an answer that never left has to say so
          or somebody waits for a response to a message nobody received.
        */}
        {message.kind === 'STUDIO' && !message.emailSent ? (
          <span className="text-[11.5px]" style={{ color: 'var(--danger)' }}>
            Not delivered — the mail server refused it
          </span>
        ) : null}
      </span>
      {/* `pre-wrap`: what somebody typed had paragraphs in it. */}
      {/*
        `break-words` beside `pre-wrap`: a report can carry a log line, a
        base64 blob or a URL longer than the panel, and a word with nowhere
        to break pushes the whole column sideways.
      */}
      <p className="m-0 text-[13.5px] leading-[1.6] break-words whitespace-pre-wrap">
        {message.body}
      </p>
    </article>
  );
}

/** The box that writes back, and the box that writes to nobody. */
function Compose({ ticket }: { ticket: TicketDetail }) {
  const [reply, setReply] = useState('');
  const [note, setNote] = useState('');
  const [after, setAfter] = useState<TicketStatus>('WAITING');
  const sendReply = useReplyToTicket();
  const addNote = useAddTicketNote();
  /*
   * ⚠️ A latch, not `isPending`, and not the disabled attribute. Both of
   * those are state: they are true on the *next* render, and every click in
   * the same tick — a double-click, a key held down — goes straight past
   * them. Measured at three presses, three replies, three emails to
   * somebody who asked one question. The API has no idea they are the same
   * answer, because they are three separate ones.
   */
  const sending = useRef(false);

  const send = () => {
    if (!reply.trim() || sending.current || sendReply.isPending) return;
    sending.current = true;
    sendReply.mutate(
      { input: { ticketId: ticket.id, body: reply.trim(), status: after } },
      {
        onSettled: () => {
          sending.current = false;
        },
        onSuccess: () => {
          setReply('');
          /*
           * And back to the safe answer. Left alone, somebody who closed a
           * report last time found "Closed" waiting for the next one — a
           * default that quietly became "this ends the conversation".
           */
          setAfter('WAITING');
        },
      },
    );
  };

  const keeping = useRef(false);
  const keep = () => {
    if (!note.trim() || keeping.current || addNote.isPending) return;
    keeping.current = true;
    addNote.mutate(
      { input: { ticketId: ticket.id, body: note.trim() } },
      {
        onSettled: () => {
          keeping.current = false;
        },
        onSuccess: () => setNote(''),
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className={panel} style={panelStyle}>
        <h2 className="eyebrow m-0">Answer</h2>
        <textarea
          aria-label="Your answer"
          value={reply}
          onChange={(event) => setReply(event.target.value)}
          rows={7}
          maxLength={10_000}
          placeholder={`Write to ${ticket.name ?? ticket.email}. They get this by email, with the reference in the subject.`}
          className="field field-modal resize-y"
        />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-[12.5px]">
            {/*
              Set with the reply rather than separately: an answer almost
              always means one of these two, and a status somebody has to
              remember to change afterwards is a list that stops telling the
              truth within a week.

              The name is in a `<span>` rather than loose in the label: a
              label whose first element child is the control itself is what
              `field-labels.spec` refuses, because the name then reads as
              the control's own contents.
            */}
            <span className="field-label">Then mark it</span>
            <select
              aria-label="Then mark it"
              value={after}
              onChange={(event) => setAfter(event.target.value as TicketStatus)}
              className="field field-modal py-1.5"
            >
              <option value="WAITING">Waiting on them</option>
              <option value="OPEN">Open</option>
              <option value="CLOSED">Closed</option>
            </select>
          </label>
          <button
            type="button"
            onClick={send}
            disabled={!reply.trim() || sendReply.isPending}
            className="btn-accent focus-ring"
          >
            {sendReply.isPending ? 'Sending…' : 'Send the answer'}
          </button>
        </div>
        {sendReply.isError ? (
          <p className="m-0 text-[12.5px]" role="alert" style={{ color: 'var(--danger)' }}>
            {/*
              Not "nothing was sent". The API writes a reply down before it
              emails it, so a connection lost on the way back leaves an
              answer that exists — and telling somebody to try again would
              send it twice, to them and to the person waiting.
            */}
            That did not come back. The report has been reloaded — check whether the answer is there
            before sending it again.
          </p>
        ) : null}
      </div>

      <div className={panel} style={panelStyle}>
        <h2 className="eyebrow m-0">Note</h2>
        <p className="m-0 text-[12px]" style={{ color: 'var(--dim)' }}>
          Kept with the report. Never sent to anybody.
        </p>
        <textarea
          aria-label="A note for the studio"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={3}
          maxLength={10_000}
          className="field field-modal resize-y"
        />
        <div className="flex items-center justify-end gap-3">
          {addNote.isError ? (
            <p className="m-0 text-[12.5px]" role="alert" style={{ color: 'var(--danger)' }}>
              That note was not saved. Try again.
            </p>
          ) : null}
          <button
            type="button"
            onClick={keep}
            disabled={!note.trim() || addNote.isPending}
            className="btn-outline focus-ring"
          >
            {addNote.isPending ? 'Saving…' : 'Keep the note'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function TicketPage() {
  const { id } = useParams<{ id: string }>();
  const ticket = useTicket(id);
  const setStatus = useSetTicketStatus();

  if (ticket.isPending) {
    return (
      <p className="m-0 py-10 text-center text-[13.5px]" style={{ color: 'var(--muted)' }}>
        Loading…
      </p>
    );
  }
  /*
   * ⚠️ `data` first, `isError` second. A refetch that fails after a success
   * leaves the query in `error` with the previous answer still in hand —
   * and this screen unmounting takes the reply somebody is halfway through
   * writing with it. Worse, the reply's own failure path asks for the
   * report again, so the two failures arrive together: press Send, lose
   * the answer. Nothing is thrown away while there is something to show.
   */
  if (!ticket.data) {
    return (
      <p className="m-0 py-10 text-center text-[13.5px]" style={{ color: 'var(--muted)' }}>
        That report is not here.{' '}
        <Link to="/tickets" className="prose-link focus-ring">
          Back to the reports
        </Link>
      </p>
    );
  }

  const report = ticket.data;

  return (
    <section className="flex flex-col gap-4 px-[18px] pt-3.5 pb-6">
      {/*
        Said, rather than acted on: what is on screen is the last answer
        that arrived, and somebody needs to know it may have moved since.
      */}
      {ticket.isError ? (
        <p
          className="m-0 rounded-[10px] border px-3.5 py-2.5 text-[12.5px]"
          role="status"
          style={{ borderColor: 'var(--line2)', color: 'var(--muted)' }}
        >
          Could not reload this report just now — what is below may be out of date.
        </p>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/tickets" className="focus-ring text-[13px]" style={{ color: 'var(--muted)' }}>
          ← All reports
        </Link>
        <label className="flex items-center gap-2 text-[12.5px]">
          <span className="field-label">Status</span>
          <select
            aria-label="Status"
            value={report.status}
            onChange={(event) =>
              setStatus.mutate({ id: report.id, status: event.target.value as TicketStatus })
            }
            disabled={setStatus.isPending}
            className="field field-modal py-1.5"
          >
            {TICKET_STATUSES.map((one) => (
              <option key={one.value} value={one.value}>
                {one.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {/*
        A select that snaps back to its old value says nothing about why.
        Without this the only sign of a failed change was the answer moving
        under the cursor.
      */}
      {setStatus.isError ? (
        <p className="m-0 text-[12.5px]" role="alert" style={{ color: 'var(--danger)' }}>
          That status did not change. Try again.
        </p>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="flex flex-col gap-4">
          <div className={panel} style={panelStyle}>
            <h1 className="m-0 text-[18px] leading-[1.25] font-semibold tracking-[-0.02em]">
              {report.subject}
            </h1>
            <span className="text-[12px]" style={{ color: 'var(--dim)' }}>
              <span className="font-mono">{report.reference}</span>
              {' · '}
              {TICKET_KINDS[report.kind] ?? report.kind}
              {' · '}
              {statusLabel(report.status)}
            </span>
          </div>

          <div className="flex flex-col gap-3">
            {report.messages.map((message) => (
              <Message key={message.id} message={message} />
            ))}
          </div>

          <Compose ticket={report} />
        </div>

        <aside className="flex flex-col gap-4">
          <div className={panel} style={panelStyle}>
            <h2 className="eyebrow m-0">Who and what</h2>
            <Field label="Reply to">
              {/*
                A mail link, because the conversation may have to leave this
                screen — an attachment they need to send back, a thread
                already going. The reference travels in the subject either
                way.
              */}
              <a
                href={`mailto:${encodeURIComponent(report.email)}?subject=${encodeURIComponent(
                  `Re: [${report.reference}] ${report.subject}`,
                )}`}
                className="focus-ring"
              >
                {report.email}
              </a>
            </Field>
            {report.name ? <Field label="Name">{report.name}</Field> : null}
            {/*
              The name, not a link. The Apps screen is listed by id and
              filters from its own state, so `?slug=` reached it and did
              nothing — a link that lands somewhere unfiltered is worse than
              plain text, because it looks like it failed.
            */}
            <Field label="App">
              {/*
                `||`, not `??`: the API sends the name and the slug together
                or sends neither, and an app named with an empty string would
                leave this box blank rather than falling through.
              */}
              {report.appName || report.appSlug || 'Not said — the website, or they were not sure'}
            </Field>
            {report.appVersion ? <Field label="Version">{report.appVersion}</Field> : null}
            {report.systemInfo ? <Field label="System">{report.systemInfo}</Field> : null}
            {/* The name, as every other screen here says it. */}
            <Field label="Language">{localeLabels[report.locale as Locale] ?? report.locale}</Field>
            <Field label="Arrived">{moment(report.createdAt)}</Field>
            {report.closedAt ? <Field label="Closed">{moment(report.closedAt)}</Field> : null}
            {/*
              Only when reCAPTCHA had an opinion. A number beside every
              report would invite reading it as a verdict, and it is not one
              — it is why this report is where it is.
            */}
            {report.spamScore !== null ? (
              <Field label="reCAPTCHA score">{report.spamScore.toFixed(2)}</Field>
            ) : null}
          </div>

          {report.attachmentUrl ? (
            <div className={panel} style={panelStyle}>
              <h2 className="eyebrow m-0">Screenshot</h2>
              {/*
                A link around it, so the full size opens in a tab — a
                screenshot of a whole window is unreadable at 268 pixels, and
                that is usually the width of the thing being complained
                about.
              */}
              <a
                href={report.attachmentUrl}
                target="_blank"
                rel="noreferrer"
                className="focus-ring"
              >
                <img
                  src={report.attachmentUrl}
                  alt="The screenshot sent with this report"
                  className="w-full rounded-[10px] border"
                  style={{ borderColor: 'var(--line2)' }}
                />
              </a>
            </div>
          ) : null}
        </aside>
      </div>
    </section>
  );
}

export default TicketPage;
